import { Plus, X } from "lucide-react";
import type {
  AgentWorkspacePolicy,
  AgentWorkspacePolicyAction,
} from "@lumen/shared";
import { t } from "../../i18n";

export function WorkspacePolicyEditor({
  value,
  onChange,
  requireFallback = false,
  disabled = false,
}: {
  value: AgentWorkspacePolicy;
  onChange: (value: AgentWorkspacePolicy) => void;
  requireFallback?: boolean;
  disabled?: boolean;
}) {
  const rows = Object.entries(value);
  const update = (
    index: number,
    pattern: string,
    action: AgentWorkspacePolicyAction,
  ) => {
    const next = rows.map(([currentPattern, currentAction], rowIndex) =>
      rowIndex === index
        ? ([pattern, action] as const)
        : ([currentPattern, currentAction] as const),
    );
    onChange(Object.fromEntries(next.filter(([key]) => key.trim())));
  };
  return (
    <div className="space-y-2">
      {rows.map(([pattern, action], index) => (
        <div
          key={`${index}-${pattern}`}
          className="grid grid-cols-[1fr_100px_auto] gap-2"
        >
          <input
            className="field font-mono text-xs"
            disabled={disabled || (pattern === "*" && requireFallback)}
            value={pattern}
            placeholder={t("command pattern")}
            onChange={(event) => update(index, event.target.value, action)}
          />
          <select
            className="field py-2 text-xs font-semibold"
            disabled={disabled}
            value={action}
            onChange={(event) =>
              update(
                index,
                pattern,
                event.target.value as AgentWorkspacePolicyAction,
              )
            }
          >
            <option value="allow">{t("Allow")}</option>
            <option value="ask">{t("Ask")}</option>
            <option value="deny">{t("Deny")}</option>
          </select>
          <button
            type="button"
            className="button p-2 text-stone-400 hover:text-red-600"
            disabled={disabled || (pattern === "*" && requireFallback)}
            onClick={() =>
              onChange(
                Object.fromEntries(
                  rows.filter((_, rowIndex) => rowIndex !== index),
                ),
              )
            }
          >
            <X size={15} />
          </button>
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          className="button text-xs"
          onClick={() =>
            onChange({ ...value, [`command ${rows.length + 1}*`]: "ask" })
          }
        >
          <Plus size={14} /> {t("Add rule")}
        </button>
      )}
      <p className="text-[11px] leading-5 text-stone-400">
        {t("The most specific matching glob wins.")}
        <code>*</code> {t("is the fallback.")}
      </p>
    </div>
  );
}
